import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { services } from '../data/mockData';
import { User, Zap, Activity, Trophy, ArrowRight, Check, Apple, HeartPulse } from 'lucide-react';

const Servizi = () => {
  const iconMap = {
    User,
    Zap,
    Activity,
    Trophy,
    Apple,
    HeartPulse
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48"
            alt="Training Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent"></div>
        </div>

        <div className="relative container mx-auto px-4 py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              I Nostri <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Servizi</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Ogni servizio è costruito su misura per portarti dove vuoi arrivare. Scegli il tuo percorso.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-950">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              const isEven = index % 2 === 0;

              return (
                <div key={service.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${!isEven ? 'lg:grid-flow-dense' : ''}`}>
                  {/* Image */}
                  <div className={`relative ${!isEven ? 'lg:col-start-2' : ''}`}>
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-green-400 blur-3xl opacity-20"></div>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="relative rounded-2xl shadow-2xl w-full h-64 sm:h-80 lg:h-96 object-cover"
                    />
                  </div>

                  {/* Content */}
                  <div className={!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}>
                    <Card className="bg-white/5 border border-white/10 backdrop-blur-sm hover:border-orange-500/50 transition-all duration-300">
                      <CardHeader>
                        {service.badge && (
                          <div className="inline-block mb-3">
                            <span className="px-3 py-1 bg-green-400/20 border border-green-400/30 rounded-full text-green-400 font-bold text-xs uppercase tracking-widest">
                              {service.badge}
                            </span>
                          </div>
                        )}
                        <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-green-400 rounded-xl flex items-center justify-center mb-4">
                          <Icon className="w-8 h-8 text-black" />
                        </div>
                        <CardTitle className="text-3xl font-black text-white mb-4">
                          {service.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                          {service.description}
                        </p>
                        
                        <div className="space-y-3 mb-8">
                          <h4 className="text-sm font-bold text-orange-500 uppercase tracking-wide">
                            Cosa Include:
                          </h4>
                          {service.benefits.map((benefit, i) => (
                            <div key={`${service.id}-benefit-${i}`} className="flex items-start space-x-3">
                              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-300">{benefit}</span>
                            </div>
                          ))}
                        </div>

                        <Link to="/contatti">
                          <Button className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-6 text-base uppercase tracking-wide group">
                            Prenota Prova Gratuita
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why It Works Section */}
      <section className="py-24 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-8">
              Perché il Nostro <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Metodo Funziona</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-12">
              <Card className="bg-white/5 border border-white/10 backdrop-blur-sm">
                <CardContent className="p-6 md:p-8 text-center">
                  <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400 mb-4">
                    01
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Valutazione Iniziale</h3>
                  <p className="text-gray-400">
                    Analizziamo obiettivi, storia fisica, limitazioni e costruiamo il tuo programma unico.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border border-white/10 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400 mb-4">
                    02
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Esecuzione Guidata</h3>
                  <p className="text-gray-400">
                    Ogni sessione segue il piano. Supervisione totale, correzioni continue, progressione controllata.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border border-white/10 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400 mb-4">
                    03
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Monitoraggio & Adattamento</h3>
                  <p className="text-gray-400">
                    Misuriamo progressi settimanali e adattiamo il programma per massimizzare i risultati.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Transparency */}
      <section className="py-24 bg-gradient-to-b from-zinc-950 to-black">
        <div className="container mx-auto px-4">
          {/* Glass Section - Performance Programs */}
          <div className="max-w-5xl mx-auto mb-16">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                Programmi <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Performance</span>
              </h2>
              <p className="text-xl text-gray-400">
                Metodologie avanzate per risultati superiori
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* HIIT Performance */}
              <Card className="bg-white/5 backdrop-blur-xl border border-white/10 hover:border-orange-500/50 transition-all duration-300 group">
                <CardContent className="p-6 md:p-8">
                  <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-green-400 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <svg className="w-7 h-7 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-black text-white mb-3">HIIT Performance</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Allenamenti ad alta intensità per massimizzare la capacità cardiovascolare e la potenza esplosiva.
                  </p>
                </CardContent>
              </Card>

              {/* Functional Strength */}
              <Card className="bg-white/5 backdrop-blur-xl border border-white/10 hover:border-orange-500/50 transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-green-400 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <svg className="w-7 h-7 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-black text-white mb-3">Functional Strength</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Costruzione di forza funzionale attraverso movimenti multi-articolari e pattern naturali.
                  </p>
                </CardContent>
              </Card>

              {/* Return to Play */}
              <Card className="bg-white/5 backdrop-blur-xl border border-white/10 hover:border-orange-500/50 transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-green-400 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <svg className="w-7 h-7 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-black text-white mb-3">Return to Play</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Protocolli di ritorno all'attività sportiva post-infortunio con progressioni sicure e misurabili.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Investimento nella Tua <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Trasformazione</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Non pubblicizziamo prezzi online. Ogni percorso è personalizzato, ogni investimento è diverso.
            </p>
            <Card className="bg-white/5 border border-orange-500/50 backdrop-blur-sm">
              <CardContent className="p-10">
                <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                  Durante la <span className="text-orange-500 font-bold">prova gratuita</span>, valuteremo insieme i tuoi obiettivi e costruiremo un piano su misura. Solo allora discuteremo l'investimento necessario.
                </p>
                <p className="text-gray-400 mb-8">
                  Una cosa è certa: il ritorno sarà incommensurabile. Salute, forza, disciplina, fiducia. Questi non hanno prezzo.
                </p>
                <Link to="/contatti" className="inline-block w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-6 sm:px-10 py-6 sm:py-7 text-base sm:text-lg uppercase tracking-wide shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 whitespace-normal">
                    Richiedi la Prova Gratuita
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Servizi;
